import React, { ReactNode, useMemo, useState } from "react";
import { api } from "api/api";
import {
  ExcelExportRequestParams,
  defaultExcelExportRequestParams,
} from "types/excel";
import { getFilenameFromContentDisposition, saveBlobToFile } from "api/helpers";
import {
  Checkbox,
  DialogContentText,
  FormControlLabel,
  FormGroup,
  Stack,
} from "@mui/material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { ActionButtonConfirmStatusWithPolling } from "components";
import { UseProcessPollingOptions } from "hooks/useProcessPolling";
import { ExcelExportProcess, ProcessState } from "types/process";
import { toast } from "react-toastify";
import { DownloadActionResult } from "components/ActionResult/DownloadActionResult";

export interface ExportProductlineToExcelButtonProps {
  productLineId?: string;
  propertyId?: string;
  propertyName?: string;
}

export const ExportPropertyToExcelButton: React.FC<
  ExportProductlineToExcelButtonProps
> = ({ productLineId, propertyId, propertyName }) => {
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const [exportIds, setExportIds] = useState<boolean>(true);
  const [exportSubproperties, setExportSubproperties] = useState<boolean>(true);
  const [buildTableOfContents, setBuildTableOfContents] =
    useState<boolean>(false);

  /** BEGIN ActionButtonConfirmStatusWithPolling Implementation */

  /**
   * Reset local state when the dialog opens
   */
  const onDialogOpen = () => {
    setDownloadStatus(null);
    setExportIds(true);
    setExportSubproperties(true);
    setBuildTableOfContents(false);
  };

  /**
   * startRequest:
   *    - Called once when the user confirms the action (clicks OK/Export).
   *    - Should start a backend process and return the initial process object.
   *    - Throw to surface an immediate validation/setup error in the dialog.
   */
  const startRequest = async (): Promise<ExcelExportProcess> => {
    if (!productLineId) {
      throw new Error("No product line selected.");
    }
    if (!propertyId) {
      throw new Error("No property selected.");
    }

    //Create parameters for the excel export
    // Export the property passed as a prop and all of its subproperties.
    const exportParams: ExcelExportRequestParams = {
      ...defaultExcelExportRequestParams,
      propertyIds: [propertyId],
      exportSubproperties: exportSubproperties,
      tableIds: [],
      exportIds: exportIds,
      buildTableOfContents: buildTableOfContents,
      zipIfSingleFile: false,
    };

    //Start Export to Excel
    const response = await api.exportToExcel(productLineId, exportParams);
    return response.data;
  };

  /**
   * pollRequest:
   *    - Polls the server for updated process state by process id.
   *    - Called repeatedly (pollIntervalMs) until the process reaches a terminal state.
   *    - Must return the latest process object by id.
   */
  const pollRequest = (id: string): Promise<ExcelExportProcess> => {
    const response = api
      .getProcess(id)
      .then((r) => r.data as ExcelExportProcess);
    return response;
  };

  /**
   * Called once after the process reaches a terminal state.
   * On success, downloads the generated export file.
   * onTerminal:
   *    - Called once after polling ends (terminal state reached).
   *    - Typical use: download output files, show toasts, do follow-up API calls.
   *    - Throw to mark the dialog as ERROR (ModalDialog will surface the error).
   */
  const onTerminal = async (process: ExcelExportProcess): Promise<void> => {
    // Only download on successful completion
    if (process.processState !== ProcessState.COMPLETE) return;

    // Should be impossible if startRequest already ran, but safe-guard anyway.
    if (!productLineId) {
      throw new Error("No product line selected.");
    }
    if (!propertyId) {
      throw new Error("No property selected.");
    }

    const serverFileName = process.fileName;
    if (!serverFileName) {
      throw new Error(
        "Export completed, but no fileName was provided by the process."
      );
    }

    setDownloadStatus("In Process...");

    try {
      const response = await api.getExcelExport(productLineId, serverFileName);

      if (response.status >= 400) {
        // preserve server error message if it’s a string
        const serverMessage =
          typeof response.data === "string" ? response.data : null;

        throw new Error(
          serverMessage ||
            `Failed to download export (HTTP ${response.status}).`
        );
      }

      // It is lowercase in local and sometimes capitalized when deployed
      const contentDisposition =
        response.headers?.["content-disposition"] ||
        response.headers?.["Content-Disposition"] ||
        null;

      const downloadName =
        getFilenameFromContentDisposition(contentDisposition) ?? serverFileName;

      saveBlobToFile(response.data, downloadName);

      setDownloadStatus("Completed - check browser downloads");
      toast.success(`'${downloadName}' saved to browser downloads`);
    } catch (e) {
      setDownloadStatus("Failed");
      throw e; // keep the dialog in ERROR state (ModalDialog derives error/message)
    }
  };

  /**
   * Options object consumed by useProcessPolling (via ActionButtonConfirmStatusWithPolling).
   * Keep this object as “wiring only” to stay readable.
   */
  const processPollingOptions: UseProcessPollingOptions<
    ExcelExportProcess,
    ExcelExportProcess
  > = {
    startRequest,
    pollRequest,
    pollIntervalMs: 2000,
    onTerminal,
  };

  // Build the content section for the dialog
  const contentSection = useMemo<ReactNode>(() => {
    return (
      <Stack spacing={2}>
        <DialogContentText>
          Clicking on the <strong>OK</strong> button below will export property{" "}
          <strong>{propertyName}</strong> and all of its subproperties to an
          Excel file.
        </DialogContentText>

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={buildTableOfContents}
                onChange={(e) => setBuildTableOfContents(e.target.checked)}
              />
            }
            label="Include Table of Contents"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={exportSubproperties}
                onChange={(e) => setExportSubproperties(e.target.checked)}
              />
            }
            label="Include Subproperties"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={exportIds}
                onChange={(e) => setExportIds(e.target.checked)}
              />
            }
            label="Export IDs"
          />
        </FormGroup>
      </Stack>
    );
  }, [exportIds, exportSubproperties, buildTableOfContents, propertyName]);

  //Callback for the process polling hook to generate results on process completion
  const buildResultSection = (process: ExcelExportProcess): ReactNode => {
    return DownloadActionResult(process, {
      filename: process.fileName,
      downloadStatus,
      errorMessage: downloadStatus,
    });
  };

  /** END ActionButtonConfirmStatusWithPolling Implementation */

  return (
    <ActionButtonConfirmStatusWithPolling<
      ExcelExportProcess,
      ExcelExportProcess
    >
      //Button Props
      variant="outlined"
      color="primary"
      disabled={false}
      //buttonFullWidth={true}
      //Icon Props
      icon={TableChartOutlinedIcon}
      iconOnly={true}
      //Tooltip Props
      tooltipText="Export Property to Excel"
      tooltipArrow={true}
      tooltipPlacement="bottom"
      //Dialog Props
      maxWidth="sm"
      fullWidth
      dialogTitle="Export Property to Excel"
      disableEscapeKeyDown={true}
      disableCloseOnBackdropClick={true}
      //Content Section Props
      contentSection={contentSection}
      hideContentOnProcessComplete={true}
      //Action Button Props
      defaultButton="OK"
      cancelButtonText="Cancel"
      okButtonText="Export"
      //okButtonWarningColor
      //Process Polling Options
      processPollingOptions={processPollingOptions}
      buildResultSection={buildResultSection}
      onDialogOpen={onDialogOpen}
      resetOnOpen={true}
    >
      Export to Excel
    </ActionButtonConfirmStatusWithPolling>
  );
};
